import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../core/store/store';
import UserRolesManagementContent from '../components/UserRolesManagementContent';
import { getApiBaseUrl } from '../../../../core/config/api';
import userService, { UserWithRole } from '../../../../shared/services/userService';
import UserDetailsModal from '../components/modals/UserDetailsModal';
import SanctionModal from '../components/modals/SanctionModal';

type PanelUsersRolesV1Props = {
  mode?: 'users' | 'moderation';
};

const PanelUsersRolesV1: React.FC<PanelUsersRolesV1Props> = ({ mode = 'users' }) => {
  const isModeration = mode === 'moderation';
  const { currentUser } = useSelector((state: RootState) => state.user);
  const [rows, setRows] = React.useState<Array<{
    id: number;
    username: string;
    email: string;
    roles_id: number;
    activated: number;
    argents: number;
    medailles: number;
    total_batiments: number;
    batiments_details: string;
    total_vehicules: number;
    vehicules_details: string;
    total_personnel: number;
    personnel_details: string;
    total_interventions: number;
  }>>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [onlineUserIds, setOnlineUserIds] = React.useState<Set<number>>(new Set());
  const [selectedUser, setSelectedUser] = React.useState<UserWithRole | null>(null);
  const [showDetailsModal, setShowDetailsModal] = React.useState(false);
  const [showSanctionModal, setShowSanctionModal] = React.useState(false);
  const [resettingPassword, setResettingPassword] = React.useState(false);
  const [updating, setUpdating] = React.useState(false);

  React.useEffect(() => {
    if (!isModeration) return;
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Token manquant');
        const response = await fetch(`${getApiBaseUrl(true)}/panel/v1/moderation/possessions?page=1&limit=100`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (!response.ok || !data?.success) throw new Error(data?.error || 'Erreur chargement possessions');
        setRows(Array.isArray(data.data) ? data.data : []);
      } catch (e: any) {
        setError(e?.message || 'Erreur');
      } finally {
        setLoading(false);
      }
    };
    void run();
  }, [isModeration]);

  React.useEffect(() => {
    if (!isModeration) return;
    const loadOnline = async () => {
      try {
        const response = await userService.getOnlineUsers();
        if (response.success && response.data) {
          setOnlineUserIds(new Set(response.data.map((u) => u.user_id)));
        }
      } catch {
        // ignore
      }
    };
    void loadOnline();
    const timer = window.setInterval(() => {
      void loadOnline();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [isModeration]);

  const openUserDetails = async (userId: number) => {
    try {
      const response = await userService.getUserById(userId);
      if (response.success && response.data) {
        setSelectedUser(response.data);
        setShowDetailsModal(true);
      }
    } catch (e: any) {
      setError(e?.message || 'Erreur chargement détails joueur');
    }
  };

  const openSanction = async (userId: number) => {
    try {
      const response = await userService.getUserById(userId);
      if (response.success && response.data) {
        setSelectedUser(response.data);
        setShowSanctionModal(true);
      }
    } catch (e: any) {
      setError(e?.message || 'Erreur chargement joueur');
    }
  };

  const handleForceLogout = async (userId: number) => {
    try {
      setUpdating(true);
      await userService.forceLogoutUser(userId);
      setOnlineUserIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    } catch (e: any) {
      setError(e?.message || 'Erreur déconnexion forcée');
    } finally {
      setUpdating(false);
    }
  };

  const handleResetPassword = async (userId: number) => {
    try {
      setResettingPassword(true);
      await userService.resetUserPassword(userId);
    } catch (e: any) {
      setError(e?.message || 'Erreur reset mot de passe');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleSanction = async (
    type: 'ban_permanent' | 'ban_temporary' | 'mute' | 'unban' | 'unmute',
    _duration?: number,
    reason?: string,
  ) => {
    if (!selectedUser) return;
    try {
      setUpdating(true);
      const activated = type === 'unban' || type === 'unmute';
      await userService.updateUserAccess(selectedUser.id, activated, reason);
      setShowSanctionModal(false);
      await openUserDetails(selectedUser.id);
    } catch (e: any) {
      setError(e?.message || 'Erreur sanction');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="rounded-lg border border-[#263455] bg-[#0b1734] p-4">
      <h2 className="mb-4 text-xl font-semibold text-white">
        {isModeration ? 'Modération en jeu' : 'Joueurs & gestion complète'}
      </h2>
      {isModeration ? (
        <div className="space-y-3">
          <p className="text-sm text-slate-300">Vue des possessions des joueurs (mondial).</p>
          {loading && <p className="text-slate-300">Chargement...</p>}
          {error && <p className="text-red-300">{error}</p>}
          {!loading && !error && (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {rows.map((row) => (
                <div key={row.id} className="rounded-lg border border-[#2b3a61] bg-[#0d1b3a] p-3">
                  <div className="mb-2 flex items-start justify-between">
                    <div>
                      <div className="font-semibold text-slate-100">{row.username}</div>
                      <div className="text-xs text-slate-400">{row.email}</div>
                    </div>
                    <div className={`rounded px-2 py-1 text-xs ${onlineUserIds.has(row.id) ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-500/20 text-slate-300'}`}>
                      {onlineUserIds.has(row.id) ? 'En ligne' : 'Hors ligne'}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded border border-[#33456f] bg-[#101f44] p-2">Bâtiments: <span className="text-slate-100">{row.total_batiments}</span></div>
                    <div className="rounded border border-[#33456f] bg-[#101f44] p-2">Véhicules: <span className="text-slate-100">{row.total_vehicules}</span></div>
                    <div className="rounded border border-[#33456f] bg-[#101f44] p-2">Personnel: <span className="text-slate-100">{row.total_personnel}</span></div>
                    <div className="rounded border border-[#33456f] bg-[#101f44] p-2">Interventions: <span className="text-slate-100">{row.total_interventions}</span></div>
                    <div className="rounded border border-[#33456f] bg-[#101f44] p-2">Argent: <span className="text-slate-100">{Number(row.argents || 0).toLocaleString('fr-FR')}</span></div>
                    <div className="rounded border border-[#33456f] bg-[#101f44] p-2">Médailles: <span className="text-slate-100">{Number(row.medailles || 0).toLocaleString('fr-FR')}</span></div>
                  </div>

                  <div className="mt-2 space-y-1 text-xs">
                    <div><span className="text-slate-400">Détail bâtiments:</span> <span className="text-slate-200">{row.batiments_details || '—'}</span></div>
                    <div><span className="text-slate-400">Détail véhicules:</span> <span className="text-slate-200">{row.vehicules_details || '—'}</span></div>
                    <div><span className="text-slate-400">Détail personnel:</span> <span className="text-slate-200">{row.personnel_details || '—'}</span></div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <button type="button" onClick={() => openUserDetails(row.id)} className="rounded bg-cyan-600 px-2.5 py-1 text-xs text-white hover:bg-cyan-700">
                      Voir détails
                    </button>
                    <button type="button" onClick={() => openSanction(row.id)} className="rounded bg-red-600 px-2.5 py-1 text-xs text-white hover:bg-red-700">
                      Sanctionner
                    </button>
                    {onlineUserIds.has(row.id) && (
                      <button type="button" disabled={updating} onClick={() => handleForceLogout(row.id)} className="rounded bg-orange-600 px-2.5 py-1 text-xs text-white hover:bg-orange-700 disabled:opacity-60">
                        Déco forcée
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <UserRolesManagementContent
          defaultUserType="all"
          mode="users"
        />
      )}
      {showDetailsModal && selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedUser(null);
          }}
          onResetPassword={handleResetPassword}
          resettingPassword={resettingPassword}
          onSanction={(userId) => openSanction(userId)}
          onForceLogout={handleForceLogout}
          currentUserRoleId={currentUser?.roles_id}
          currentUserId={currentUser?.id}
          onlineUserIds={onlineUserIds}
          context="users"
        />
      )}
      {showSanctionModal && selectedUser && (
        <SanctionModal
          user={selectedUser}
          updating={updating}
          onSanction={handleSanction}
          onCancel={() => {
            setShowSanctionModal(false);
            setSelectedUser(null);
          }}
          currentUserRoleId={currentUser?.roles_id}
          context="users"
        />
      )}
    </div>
  );
};

export default PanelUsersRolesV1;
